import { Sparkles, Upload, Copy, Plus, Pencil, RefreshCw, CalendarCheck, ListChecks, Mic, ArrowRightCircle } from 'lucide-react';
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { SampleDownloadButtons } from './SampleDownloadButtons';

function Section({ icon: Icon, title, children }) {
    return (
        <div className="space-y-3">
            <div className="flex items-center gap-2">
                <Icon className="h-4 w-4 text-primary shrink-0" />
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            </div>
            <div className="pl-6 space-y-2 text-sm text-muted-foreground">
                {children}
            </div>
        </div>
    );
}

function Step({ label, desc }) {
    return (
        <div className="flex gap-2">
            <ArrowRightCircle className="h-3.5 w-3.5 text-primary/60 shrink-0 mt-0.5" />
            <div>
                <span className="font-medium text-foreground">{label}</span>
                {desc && <span className="text-muted-foreground"> — {desc}</span>}
            </div>
        </div>
    );
}

/**
 * QuestionBankHelpDrawer — slide-over that explains all Question Bank workflows.
 *
 * Props:
 *   open    boolean
 *   onClose () => void
 */
export function QuestionBankHelpDrawer({ open, onClose }) {
    return (
        <Sheet open={open} onOpenChange={v => !v && onClose()}>
            <SheetContent side="right" className="w-full sm:max-w-[400px] overflow-y-auto">
                <SheetHeader className="mb-6">
                    <SheetTitle className="text-base">How to use Question Bank</SheetTitle>
                    <SheetDescription className="text-xs">
                        Everything you need to build and manage reusable question sets.
                    </SheetDescription>
                </SheetHeader>

                <div className="space-y-8 pb-8">

                    {/* Ways to create */}
                    <Section icon={Sparkles} title="Ways to create a question set">
                        <div className="space-y-3">
                            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                    <Sparkles className="h-3.5 w-3.5 text-violet-500" /> AI Generate
                                </div>
                                <p className="text-xs">Pick a job description, interview type, and difficulty. AI builds a full set in seconds.</p>
                            </div>
                            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                    <Upload className="h-3.5 w-3.5 text-blue-500" /> Upload from file
                                </div>
                                <p className="text-xs">Upload a PDF, DOCX, or TXT file with your own questions. AI fills in missing answers and keywords automatically.</p>
                            </div>
                            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                    <Copy className="h-3.5 w-3.5 text-emerald-500" /> Duplicate & edit
                                </div>
                                <p className="text-xs">Copy an existing set and modify it for a different role or difficulty level.</p>
                            </div>
                            <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                                    <Plus className="h-3.5 w-3.5 text-amber-500" /> Build manually
                                </div>
                                <p className="text-xs">Open any set's editor and click "Add question manually" to type questions one by one.</p>
                            </div>
                        </div>
                    </Section>

                    {/* File format */}
                    <Section icon={Upload} title="Upload file format">
                        <p>Your file should have question blocks separated by blank lines:</p>
                        <pre className="rounded-lg border border-border bg-muted/50 px-3 py-2.5 text-[10px] leading-relaxed font-mono overflow-x-auto whitespace-pre-wrap">{`Q: What is binary search?
TYPE: mcq
OPTION_A: O(n)
OPTION_B: O(log n)
OPTION_C: O(n²)
OPTION_D: O(1)
CORRECT: B

Q: Describe your conflict approach.
TYPE: verbal
IDEAL: Show communication skills...
KEYWORDS: communication, empathy`}</pre>
                        <p className="text-xs mt-1">
                            <span className="font-medium text-foreground">TYPE</span> defaults to <code className="bg-muted rounded px-0.5">verbal</code> if omitted.
                            <span className="font-medium text-foreground ml-2">IDEAL</span> and <span className="font-medium text-foreground">KEYWORDS</span> are optional — AI generates them if missing.
                        </p>
                        <SampleDownloadButtons size="xs" />
                    </Section>

                    {/* Managing questions */}
                    <Section icon={Pencil} title="Managing questions">
                        <Step label="Edit" desc="click the pencil icon on any card to open the full editor" />
                        <Step label="Verbal / MCQ toggle" desc="each question can independently be verbal or multiple-choice" />
                        <Step label="Reorder" desc="use the ↑ ↓ arrows to change question order before saving" />
                        <Step label="Regenerate one" desc="AI replaces a single question while keeping the rest" />
                        <Step label="Regenerate all" desc="full AI replacement — uses the same type, difficulty, and count" />
                        <Step label="Add manually" desc="'Add question manually' button at the bottom of the editor" />
                        <div className="mt-2 rounded-lg border border-amber-500/25 bg-amber-500/8 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                            All edits are local until you click <strong>Save set</strong>.
                        </div>
                    </Section>

                    {/* MCQ questions */}
                    <Section icon={ListChecks} title="MCQ questions">
                        <p>When a question is set to MCQ:</p>
                        <Step label="Candidate sees" desc="A / B / C / D option buttons on their interview screen" />
                        <Step label="Recording still runs" desc="candidate selects an option AND records their reasoning aloud" />
                        <Step label="Auto-scored" desc="correct option = 100, wrong = 0 — no AI evaluation needed" />
                        <Step label="Verbal recording" desc="also evaluated by AI using scoring keywords from the question" />
                        <p className="mt-1 text-[11px]">The correct option is highlighted in the admin report.</p>
                    </Section>

                    {/* Scheduling */}
                    <Section icon={CalendarCheck} title="Scheduling & usage">
                        <Step label="Select to Schedule" desc="click the button on any set card to use it for a new interview round" />
                        <Step label="Questions are copied" desc="changes made after scheduling do not affect in-progress interviews" />
                        <Step label="Usage count" desc="shown on each card — how many interview sessions were created from this set" />
                        <Step label="Last used" desc="the most recent date a session was created from this set" />
                        <div className="mt-2 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground flex gap-2">
                            <Mic className="h-3.5 w-3.5 shrink-0 text-primary mt-0.5" />
                            AI scoring, transcription, and keyword matching happen automatically after each session ends.
                        </div>
                    </Section>

                </div>
            </SheetContent>
        </Sheet>
    );
}

export default QuestionBankHelpDrawer;
